// === src/Components/newDesign/HelpModal.jsx ===
import React, { useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { BsQuestionCircle } from "react-icons/bs";

/**
 * HelpModal
 * - Shows a welcome / how-to modal on first load
 * - When dismissed, shrinks into a small floating question-mark button
 * - Clicking the button re-opens the modal
 */
export default function HelpModal({ isOpen, onClose, onOpen }) {
  const dialogRef = useRef(null);

  // Close on Escape
  useEffect(() => {
    function onKeyDown(e) {
      if (e.key === "Escape" && isOpen) onClose();
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isOpen, onClose]);

  return (
    <>
      {/* Floating help button (question mark) */}
      <motion.button
        type="button"
        aria-label="Open welcome help"
        onClick={onOpen}
        className="fixed bottom-6 right-6 z-40 h-10 w-10 rounded-full border border-white/30 bg-black/60 backdrop-blur text-white flex items-center justify-center shadow-lg hover:bg-black/70 focus:outline-none focus:ring-2 focus:ring-white/70"
        initial={{ opacity: 0, scale: 0.8, y: 20 }}
        animate={{ opacity: isOpen ? 0 : 1, scale: isOpen ? 0.9 : 1, y: isOpen ? 10 : 0 }}
        transition={{ type: "spring", stiffness: 300, damping: 22 }}
        style={{ pointerEvents: isOpen ? "none" : "auto" }}
      >
        <BsQuestionCircle className="h-5 w-5" />
      </motion.button>

      {/* Modal overlay + content */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            {/* Backdrop */}
            <div
              className="absolute inset-0 bg-black/70 backdrop-blur-sm"
              onClick={onClose}
              aria-hidden
            />

            {/* Dialog */}
            <motion.div
              role="dialog"
              aria-modal="true"
              aria-labelledby="welcome-title"
              ref={dialogRef}
              className="relative z-10 max-w-3xl w-full rounded-2xl bg-white shadow-2xl overflow-hidden"
              initial={{ y: 30, scale: 0.98, opacity: 0 }}
              animate={{ y: 0, scale: 1, opacity: 1 }}
              exit={{ y: 20, scale: 0.98, opacity: 0 }}
              transition={{ type: "spring", stiffness: 220, damping: 24 }}
            >
              <div className="px-6 pt-6 pb-4 bg-gradient-to-r from-sky-600 to-indigo-700 text-white">
                <h2
                  id="welcome-title"
                  className="text-2xl md:text-3xl font-semibold tracking-tight"
                >
                  Welcome to the C‑Dem data playground!
                </h2>
              </div>

              <div className="px-6 py-5 text-gray-800 space-y-4 leading-relaxed">
                <p>
                  With this online tool, you can explore how Canadians think about and participate
                  in democracy. You can use the tool to make graphs that visualize key ideas or view
                  simple summaries, like averages by category.
                </p>
                <p>
                  There are two main menus to guide you: one lists outcomes, such as satisfaction
                  with democracy or levels of civic engagement, and the other lets you break these
                  down by different characteristics. For instance, you might look at how
                  satisfaction with democracy changes depending on someone’s age, education, or
                  region.
                </p>
                <p>
                  To use the tool, you choose an outcome to explore, and then, if you want, a
                  category to compare it by. For example:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>
                    I want to explore people’s{" "}
                    <span className="font-semibold">satisfaction with democracy</span> by comparing
                    responses by levels of <span className="font-semibold">education</span>.
                  </li>
                  <li>
                    I want to explore people’s{" "}
                    <span className="font-semibold">support for immigration levels</span> by
                    comparing responses by their{" "}
                    <span className="font-semibold">left‑right self‑placement</span>.
                  </li>
                  <li>
                    I want to explore people’s{" "}
                    <span className="font-semibold">confidence in the federal government</span> by
                    comparing responses by their{" "}
                    <span className="font-semibold">level of news consumption</span>.
                  </li>
                </ul>
                <p className="mt-2">
                  <span className="font-semibold">Outcome:</span> “What do you want to explore?”
                </p>
              </div>

              <div className="px-6 pb-6 flex items-center justify-end gap-3 bg-gray-50">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 rounded-lg bg-gray-900 text-white hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-700"
                >
                  Got it
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
